import _ from 'underscore';
import {
  AUDIO_PRESENTER,
  BANNER,
  CAMERA_PRESENTER,
  IMAGE_FILE
} from '@shoflo/polymer';
import {
  sceneBuilder,
  buildPresenterBannerLayerSource,
  getPresenterBannerTemplate,
  isPresenterBannerLayerSource
} from '@studio/program';
import { logProgramChange } from '@shoflo/studio-redux/utils';
import { getCameraPresenterSources } from '@studio/sources';
import { sendToDatadog } from '@studio/logger';

import { BROADCAST_SCENE_TAKE_LIVE } from 'actions/Types';
import SceneLayouts, { SINGLE } from 'constants/SceneLayouts';
import {
  setProgram,
  setSceneLayout,
  updateSourcesCameraStatus,
  updateSourcesMicStatus
} from 'actions/Broadcast';
import toggleMultipleAgoraAttendeeMuteStatuses from 'actions/Broadcast/attendees/toggleMultipleAgoraAttendeeMuteStatuses';
import {
  attachAudioPresenterSources,
  getStartedOn,
  isAudioVideoSource,
  hasSameAudioVideoSource,
  getProgramLayers,
  isItemValid
} from 'utils/broadcastSourceHelper';
import {
  reValidateAudioVideoSource,
  getSceneSourceMuteStatus
} from '@shoflo/studio-redux/actions';
import { SCENE_LAYOUT_CHANGE } from 'store/eventConstants';
import { withCurrentParams } from 'actions/utils';
import { DIRECTOR } from 'constants/BroadcastLogViews';
import { VENDOR_AGORA } from '@studio/constants';

export default (scene, isTransient = false, config = {}) => {
  return withCurrentParams((dispatch, { broadcastId }, getState, socket) => {
    const state = getState();

    const {
      broadcastSettings: { autoMutePresenters },
      broadcastSources,
      attendeeMuteStatus,
      sceneLayers,
      banners,
      program: currentProgram,
      meeting: { meetingResponse, attendeeResponse },
      brandingSettings
    } = state;

    const action = {
      type: BROADCAST_SCENE_TAKE_LIVE,
      payload: scene
    };
    // don't set program. Just update the value of sceneTakenToLive in state
    if (!isTransient) {
      const { syncedLayers, syncedStyle } = getSyncedSources(scene, state);

      const { _id, description, title, layout, layers } = scene;
      let sceneLayout = null;
      if (layout) {
        sceneLayout = [...SceneLayouts, SINGLE].find(sl => sl.id === layout);
      }

      const meetingId = meetingResponse?.Meeting?.MeetingId;
      const programCamSources = getCameraPresenterSources(
        currentProgram.layers
      );
      const sceneCamSources = getCameraPresenterSources(layers);

      Object.entries(programCamSources).forEach(([srcID, src]) => {
        if (!sceneCamSources[srcID]) {
          const logPayload = {
            'studio.actionName': 'Take Scene Live',
            'studio.attendeeTileStatus': 'OFF-STAGE',
            'studio.tileAttendeeId': src.value,
            title: `ProgramUpdate: Attendee ${src.value} in meeting ${meetingId} is removed from the stage`
          };

          sendToDatadog('program-update', logPayload, logPayload.title, false);
        }
      });

      Object.entries(sceneCamSources).forEach(([srcID, src]) => {
        if (!programCamSources[srcID]) {
          const logPayload = {
            'studio.actionName': 'Take Scene Live',
            'studio.attendeeTileStatus': 'ON-STAGE',
            'studio.tileAttendeeId': src.value,
            title: `ProgramUpdate: Attendee ${src.value} in meeting ${meetingId} is added to the stage`
          };

          sendToDatadog('program-update', logPayload, logPayload.title, false);
        }
      });

      let program = {
        _id,
        description,
        layout,
        layers: syncedLayers,
        style: syncedStyle,
        title
      };

      dispatch(updateSourcesCameraStatus(layers, config));

      const currentProgramLayers = getProgramLayers(currentProgram);

      const currentPresenterBannerTemplateVars = currentProgramLayers.reduce(
        (memo, layer) => {
          const source = layer?.source;

          return source?.type === BANNER
            ? { ...memo, [source?._id]: source.value.templateVars }
            : memo;
        },
        {}
      );

      // Ensure cam status for program layers is set to true
      program = {
        ...program,
        layers: layers.map(ls => {
          if (
            ls.source.type === BANNER &&
            currentPresenterBannerTemplateVars[ls.source._id]
          ) {
            ls.source.value.templateVars = {
              ...ls?.source?.value?.templateVars,
              ...currentPresenterBannerTemplateVars[ls.source._id]
            };
          }
          if (ls.source.type === CAMERA_PRESENTER) {
            ls.layer.isCameraOn = true;
          }
          return ls;
        })
      };
      const sources = program.layers
        .map(layer => layer.source)
        // Need to make sure the source is in the broadcastSources array, otherwise we'd include overlays or other source here
        .filter(
          source =>
            source.type === AUDIO_PRESENTER ||
            _.find(broadcastSources.sources, bs => bs._id === source._id)
        );
      const updatedBanners = getUpdatedBannerForProgram(program, banners);

      const sceneSourcesMuteStatus = getSceneSourceMuteStatus(
        sources,
        layers,
        attendeeMuteStatus
      );

      const withAudioPresenterSources = attachAudioPresenterSources(
        sources,
        sceneSourcesMuteStatus
      );
      const rebuiltScene = sceneBuilder(
        sceneLayout,
        withAudioPresenterSources,
        [...sceneLayers, ...updatedBanners],
        {},
        {},
        brandingSettings?.fonts
      );

      rebuiltScene._id = program._id;
      rebuiltScene.style = program.style;
      rebuiltScene.layout = program.layout;

      // Swap out the image files source in rebuiltScene with what is in program
      rebuiltScene.layers = [
        ...rebuiltScene.layers.filter(
          ({ source }) => source.type !== IMAGE_FILE
        ),
        ...program.layers.filter(({ source }) => source.type === IMAGE_FILE)
      ];

      /**
       * Check if scene has an audio or video and the audio or video is
       * also present in current program
       */
      if (hasSameAudioVideoSource(scene.layers, currentProgram.layers)) {
        const { source } = currentProgram.layers.find(({ source }) =>
          isAudioVideoSource(source)
        );
        rebuiltScene.layers = reValidateAudioVideoSource(
          rebuiltScene.layers,
          source
        );
      }

      dispatch(setProgram(rebuiltScene, false, { isFromScene: true }));
      if (autoMutePresenters) {
        if (config.vendor === VENDOR_AGORA) {
          dispatch(
            toggleMultipleAgoraAttendeeMuteStatuses(sceneSourcesMuteStatus)
          );
        } else {
          dispatch(updateSourcesMicStatus(sceneSourcesMuteStatus));
        }
      }
      logProgramChange(getState, 'takeSceneLive action');

      if (sceneLayout) {
        dispatch(setSceneLayout(sceneLayout));
        /*
        Emit to other directors the current layout so its synced across all directors
        */
        socket.emit(SCENE_LAYOUT_CHANGE, {
          broadcastId,
          layout: sceneLayout,
          notifyingDirector:
            attendeeResponse && attendeeResponse.Attendee.AttendeeId
        });
      }
    }
    dispatch(action);
  });
};

export const getUpdatedBannerForProgram = (scene, banners) => {
  let bannerHiddenStatus = {};
  scene.layers
    .filter(({ source }) => source?.type === BANNER)
    .forEach(layer => {
      bannerHiddenStatus[layer._id] = layer.source.hidden;
    });
  return banners.map(banner => {
    const updatedBanner = { ...banner };
    if (updatedBanner.source.type === BANNER) {
      let hiddenValue = banner._id in bannerHiddenStatus ? false : true;
      if (updatedBanner.source.hidden !== hiddenValue) {
        updatedBanner.source = {
          ...updatedBanner.source,
          hidden: hiddenValue
        };
      }
    }
    return updatedBanner;
  });
};

const getSyncedSources = (scene, state) => {
  const { layers, style } = scene;

  /**
   * Ensure layers here does not have duplicate sources.
   * Presenter banners will be an exception since we use one template
   * to generate individual layers for each live presenter in program.
   */
  const filteredDuplicateLayers = [];

  const isDuplicateLayer = layerId =>
    filteredDuplicateLayers.find(({ _id }) => _id === layerId);

  layers.forEach(layer => {
    const { _id: layerId, source } = layer;

    // Presenter banners layers
    if (source.type === BANNER) {
      filteredDuplicateLayers.push(layer);
    } else {
      // Other layer sources
      if (!isDuplicateLayer(layerId)) {
        filteredDuplicateLayers.push(layer);
      }
    }
  });

  const { backgrounds } = state;

  const syncedLayers = getSyncedLayers(filteredDuplicateLayers, state);

  if (_.isEmpty(style)) {
    const logPayload = {
      title: `Style is not present in scene: ${scene}`,
      view: DIRECTOR
    };

    sendToDatadog('takeSceneLive', logPayload, logPayload.title, false);
  }

  // handle background. The background is not part of the layers array of the scene
  // it is inside scene.style
  const { backgroundId } = style || {};

  const background = backgrounds.find(b => b._id === backgroundId);

  let syncedStyle = {
    ...style
  };

  if (background && !_.isEmpty(background.source.value)) {
    const imageUrl = _.isObject(background.source.value)
      ? background.source.value.url
      : background.source.value;
    syncedStyle = {
      ...syncedStyle,
      backgroundImage: `url(${imageUrl})`,
      ...background.layer.style,
      backgroundId
    };
  }

  return {
    syncedLayers,
    syncedStyle
  };
};

const findLayerSource = (state, layerId) => {
  const { overlays, banners } = state;

  return (
    banners.find(banner => banner._id === layerId) ||
    overlays.find(overlay => overlay._id === layerId)
  );
};

export const getSyncedLayers = (programLayers = [], state) => {
  const {
    liveSources,
    broadcastSources: { sources },
    banners,
    sceneLayout,
    serverTimeDelta,
    brandingSettings
  } = state;

  /** Do not allow nulls/undefined to go through */
  const layers = programLayers.filter(layer => isItemValid(layer));

  /**
   * @param {string} id
   * @returns {{}}
   */
  const findLayer = id => {
    return layers.find(({ source }) => source._id === id);
  };

  /**
   * @param {string} id
   * @returns {{}}
   */
  const findSource = id => {
    return sources.find(({ _id }) => _id === id);
  };

  return layers.map(layer => {
    // Reconstruct presenter banner, in case presenter source was edited
    const { value: sourceValue } = layer?.source;
    // Presenter source id (linking presenter banner) stashed in templateVars.
    const presenterSourceId = sourceValue?.templateVars?.LINE_3;
    const isPresenterBanner = isPresenterBannerLayerSource(sourceValue);
    if (isPresenterBanner) {
      // Get camera source associated with this presenter banner.
      const presenterLayerSource = findLayer(presenterSourceId);

      // Get source which has the updated info.
      const presenterSource = findSource(presenterSourceId);

      if (presenterLayerSource) {
        const bannerTemplate = getPresenterBannerTemplate(banners);

        if (bannerTemplate) {
          const layerSource = {
            ...presenterLayerSource,
            source: {
              ...presenterSource
            }
          };
          const updatedLayer = buildPresenterBannerLayerSource(
            bannerTemplate,
            layerSource,
            sceneLayout?.id,
            layers,
            brandingSettings?.fonts
          );

          return updatedLayer;
        }
      }

      return layer;
    }

    let source = sources.find(s => s._id === layer?.source?._id);

    if (source) {
      // the layer source is a primitive - either a videoFile, audioFile, imageFile, cameraPresenter, screenPresenter
      if (source.hidden) source.hidden = false;

      if (!liveSources.includes(source._id) && isAudioVideoSource(source)) {
        source = {
          ...source,
          value: {
            ...source.value,
            seek: 0,
            isPlaying: true,
            startedOn: getStartedOn(true, serverTimeDelta)
          }
        };
      }

      layer.source = source;
    } else {
      // the layer source is an abstract - either an overlay or banner
      const foundLayer = findLayerSource(state, layer?._id);

      if (foundLayer) {
        if (foundLayer?.source?.hidden) {
          foundLayer.source.hidden = false;
        }
        layer.source = foundLayer.source;
      }
    }

    return layer;
  });
};
